import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Snackbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import CommentIcon from '@mui/icons-material/Comment';
import CategoryIcon from '@mui/icons-material/Category';
import EventIcon from '@mui/icons-material/Event';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupIcon from '@mui/icons-material/Group';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SourceIcon from '@mui/icons-material/Source';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CircularProgress from '@mui/material/CircularProgress';
import ApiService from '../../../../services/api';
import { getSession } from '../../../../utils/authUtils';
import { useParams, useNavigate } from 'react-router-dom';
import logger from '../../../../utils/logger';
import { useAdminRole } from '../../../../contexts/AdminRoleContext';

const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(3),
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

const TitleBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const TitleIcon = styled(Box)(({ theme }) => ({
  width: '56px',
  height: '56px',
  borderRadius: '16px',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  boxShadow: '0 8px 24px rgba(59, 130, 246, 0.35)',
}));

const StatusChip = styled(Chip)(({ status }) => {
  const colors = {
    'active': { bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff' },
    'pending': { bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#fff' },
    'closed': { bg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: '#fff' },
    'new': { bg: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: '#fff' },
    'default': { bg: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)', color: '#fff' },
  };
  const normalizeStatus = (s) => {
    if (s === null || s === undefined) return '';
    if (typeof s === 'string') return s.toLowerCase();
    return String(s).toLowerCase();
  };
  const statusLower = normalizeStatus(status);
  const colorSet = colors[statusLower] || colors.default;
  return {
    background: colorSet.bg,
    color: colorSet.color,
    fontWeight: 600,
    fontSize: '12px',
    padding: '4px 8px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  };
});

const InfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  background: '#fff',
  height: '100%',
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  borderBottom: '2px solid #f0f0f0',
}));

const CardIconBox = styled(Box)(({ bgcolor }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  background: bgcolor || 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  padding: theme.spacing(1.5, 0),
  borderBottom: '1px solid #f5f5f5',
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const DetailIcon = styled(Box)(({ theme }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  backgroundColor: '#f0f4ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(1.5),
  color: '#3b82f6',
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  color: '#6b7280',
  fontWeight: 500,
  minWidth: '120px',
}));

const DetailValue = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#1f2937',
  fontWeight: 500,
  flex: 1,
}));

const ActivityItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: '12px',
  backgroundColor: '#f8fafc',
  marginBottom: theme.spacing(1.5),
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f0f4ff',
    transform: 'translateX(4px)',
  },
}));

const ActivityAvatar = styled(Avatar)(({ theme }) => ({
  width: '36px',
  height: '36px',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  fontSize: '14px',
  fontWeight: 600,
}));

const MeetingCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  background: '#fff',
  marginTop: theme.spacing(3),
}));

const ScheduleButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  color: '#fff',
  padding: '12px 32px',
  borderRadius: '12px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
  '&:hover': {
    background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
    transform: 'translateY(-2px)',
  },
  transition: 'all 0.3s ease',
}));

const BackButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 500,
  padding: '8px 16px',
}));

const LeadProfile = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { isBDM, employeeData } = useAdminRole();
  const [isScheduling, setIsScheduling] = useState(false);
  const [formState, setFormState] = useState({
    fromDate: new Date().toISOString().slice(0, 16),
    bdm: '',
  });

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bdms, setBdms] = useState([]);
  const [bdmLoading, setBdmLoading] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [convertLoading, setConvertLoading] = useState(false);
  const selfBdmLabel = employeeData?.name ? `Self (You – ${employeeData.name})` : 'Self (You)';
  const showSelfBdm = isBDM && employeeData?.id;

  useEffect(() => {
    const fetchLead = async () => {
      try {
        setLoading(true);
        setError('');
        const pickLead = (rootObj) => {
          const idStr = String(leadId);
          if (!rootObj) return null;
          if (Array.isArray(rootObj.leads)) {
            return rootObj.leads.find(l => String(l.id) === idStr) || rootObj.leads[0] || null;
          }
          if (rootObj.lead && typeof rootObj.lead === 'object') return rootObj.lead;
          const d = rootObj.data;
          if (Array.isArray(d)) {
            return d.find(l => String(l.id) === idStr) || d[0] || null;
          }
          if (d && typeof d === 'object') {
            if (Array.isArray(d.leads)) {
              return d.leads.find(l => String(l.id) === idStr) || d.leads[0] || null;
            }
            if (d.id) return d;
          }
          if (Array.isArray(rootObj)) {
            return rootObj.find(l => String(l.id) === idStr) || rootObj[0] || null;
          }
          if (rootObj.id) return rootObj;
          return null;
        };

        let res = await ApiService.get(`/admin/leads/get-leads/${leadId}`);
        let root = res?.data ?? res;
        let found = pickLead(root);

        if (!found) {
          res = await ApiService.get(`/admin/leads/get-leads?leadId=${leadId}`);
          root = res?.data ?? res;
          found = pickLead(root);
        }

        if (!found) throw new Error('Lead not found');
        setLead(found);
      } catch (e) {
        logger.error('Failed to load lead profile', e);
        setError(e?.message || 'Failed to load lead');
      } finally {
        setLoading(false);
      }
    };
    if (leadId) fetchLead();
  }, [leadId]);

  useEffect(() => {
    const fetchBdms = async () => {
      try {
        setBdmLoading(true);
        // Using get-employees to ensure we get a full list if get-bdm is too restrictive
        const res = await ApiService.get('/admin/employee/get-employees?pageSize=1000');
        const data = res?.data?.employees || res?.employees || res?.data || [];
        let list = [];
        if (Array.isArray(data)) list = data;
        setBdms(list);
      } catch (e) {
        logger.error('Failed to load BDMs', e);
      } finally {
        setBdmLoading(false);
      }
    };
    if (!isBDM) {
      fetchBdms();
    }
  }, [isBDM]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleScheduleMeeting = async () => {
    setScheduleLoading(true);
    setScheduleError('');
    try {
      if (!formState.fromDate) throw new Error('Please select a date and time');
      if (!formState.bdm) throw new Error('Please select a BDM');

      const lead_id = Number(lead?.id || lead?.lead_id || leadId);
      const employee_id = Number(formState.bdm);
      const session = getSession();
      const currentUserId = Number(
        session?.id || session?.user?.id || session?.data?.id || session?.employee_id || session?.userId
      );
      const client_id = currentUserId;

      if (!Number.isFinite(client_id)) throw new Error('Invalid current user id');
      if (!Number.isFinite(lead_id)) throw new Error('Invalid lead id');
      if (!Number.isFinite(employee_id)) throw new Error('Please select a valid BDM');

      const iso = new Date(formState.fromDate).toISOString();
      const payload = {
        lead_id,
        employee_id,
        client_id,
        date_time: iso,
        meeting_date: iso,
        created_by: Number.isFinite(currentUserId) ? currentUserId : undefined,
        user_id: Number.isFinite(currentUserId) ? currentUserId : undefined,
      };

      const res = await ApiService.post('/admin/meeting/create-meeting', payload);
      if (res) {
        setSnackbarOpen(true);
        setTimeout(() => {
          navigate('/admin-dashboard/meetings');
        }, 1500);
      } else {
        throw new Error('Failed to schedule meeting');
      }
    } catch (e) {
      const raw = e?.response?.data || e?.body || e?.message;
      const msg = typeof raw === 'string' ? raw : (raw?.message || raw?.error || raw);
      setScheduleError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setScheduleLoading(false);
    }
  };

  // Convert Lead to Client
  const handleConvertToClient = async () => {
    if (!lead || lead.type === 'client') return;

    const confirmed = window.confirm(
      `Are you sure you want to convert "${lead.business_name || lead.contact_person}" to a client?\n\nThis will change the lead type to "client" and assign an ESC ID.`
    );

    if (!confirmed) return;

    setConvertLoading(true);
    try {
      await ApiService.put(`/admin/leads/update-leads/${leadId}`, {
        type: 'client',
        status: 'active'
      });

      // Refresh lead data
      const res = await ApiService.get(`/admin/leads/get-leads/${leadId}`);
      const root = res?.data ?? res;
      const found = root?.lead || root?.data || root;
      if (found) setLead(found);

      setSnackbarOpen(true);

      // Navigate to client profile after a short delay
      setTimeout(() => {
        navigate(`/admin-dashboard/client/${leadId}`);
      }, 1500);
    } catch (e) {
      logger.error('Failed to convert lead to client:', e);
      alert('Failed to convert lead to client: ' + (e?.message || 'Unknown error'));
    } finally {
      setConvertLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Typography variant="h6" color="text.secondary">Loading lead details...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Typography variant="h6" color="error">{error}</Typography>
        </Box>
      </Container>
    );
  }

  if (!lead) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Typography variant="h6" color="text.secondary">No lead data found</Typography>
        </Box>
      </Container>
    );
  }

  const servicesList = [
    lead.is_gmb_services ? 'GMB' : null,
    lead.is_website_service ? 'Website' : null,
    lead.is_smo_services ? 'SMO' : null,
    lead.is_other_services ? 'Other' : null,
  ].filter(Boolean);

  const fullAddress = [
    lead.address_line1,
    lead.city,
    lead.state,
    lead.zip_code,
    lead.country,
  ].filter(Boolean).join(', ');

  const rawActivities = Array.isArray(lead?.activityDetails)
    ? lead.activityDetails
    : Array.isArray(lead?.activity_details)
      ? lead.activity_details
      : Array.isArray(lead?.activities)
        ? lead.activities
        : [];

  const getInitials = (name) => {
    // Handle non-string values (null, undefined, object, number)
    if (!name || typeof name !== 'string') return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Container>
      {/* Header Section */}
      <HeaderSection>
        <TitleBox>
          <TitleIcon>
            <StorefrontIcon sx={{ fontSize: 28 }} />
          </TitleIcon>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
              <Typography variant="h4" fontWeight={700} color="#1f2937">
                {lead.business_name || 'Lead Details'}
              </Typography>
              <Chip
                label="Lead"
                size="small"
                sx={{
                  bgcolor: '#e0e7ff',
                  color: '#4338ca',
                  fontWeight: 600,
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <StatusChip label={lead.status || 'New'} status={lead.status} size="small" />
              {lead.lead_source && (
                <Chip
                  icon={<SourceIcon sx={{ fontSize: 16 }} />}
                  label={lead.lead_source}
                  size="small"
                  sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 500 }}
                />
              )}
            </Box>
          </Box>
        </TitleBox>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <BackButton
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back
          </BackButton>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin-dashboard/lead/edit/${leadId}`)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 500,
              borderColor: '#3b82f6',
              color: '#3b82f6',
              '&:hover': {
                borderColor: '#2563eb',
                backgroundColor: '#f0f4ff',
              }
            }}
          >
            Edit Lead
          </Button>

        </Box>
      </HeaderSection>

      {/* Main Content Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
        {/* Lead Information Card */}
        <InfoCard>
          <CardHeader>
            <CardIconBox bgcolor="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)">
              <BusinessIcon sx={{ fontSize: 20 }} />
            </CardIconBox>
            <Typography variant="h6" fontWeight={600} color="#1f2937">Lead Information</Typography>
          </CardHeader>

          <DetailRow>
            <DetailIcon><CategoryIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Lead Type</DetailLabel>
            <DetailValue>{lead.type || '—'}</DetailValue>
          </DetailRow>

          <DetailRow>
            <DetailIcon><BusinessIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Business</DetailLabel>
            <DetailValue sx={{ color: '#3b82f6', fontWeight: 600 }}>{lead.business_name || '—'}</DetailValue>
          </DetailRow>

          <DetailRow>
            <DetailIcon><PersonIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Contact Person</DetailLabel>
            <DetailValue>{lead.contact_person || '—'}</DetailValue>
          </DetailRow>

          <DetailRow>
            <DetailIcon><EmailIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Email</DetailLabel>
            <DetailValue>{lead.email || '—'}</DetailValue>
          </DetailRow>

          <DetailRow>
            <DetailIcon><PhoneIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Phone</DetailLabel>
            <DetailValue>{lead.phone_number || '—'}</DetailValue>
          </DetailRow>

          {lead.whatsapp && (
            <DetailRow>
              <DetailIcon><PhoneIcon sx={{ fontSize: 18, color: '#25d366' }} /></DetailIcon>
              <DetailLabel>WhatsApp</DetailLabel>
              <DetailValue>{lead.whatsapp}</DetailValue>
            </DetailRow>
          )}

          {lead.alt_number && (
            <DetailRow>
              <DetailIcon><PhoneIcon sx={{ fontSize: 18 }} /></DetailIcon>
              <DetailLabel>Alt. Phone</DetailLabel>
              <DetailValue>{lead.alt_number}</DetailValue>
            </DetailRow>
          )}

          <DetailRow>
            <DetailIcon><PublicIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Website</DetailLabel>
            <DetailValue>{lead.website || '—'}</DetailValue>
          </DetailRow>

          <DetailRow>
            <DetailIcon><LocationOnIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Address</DetailLabel>
            <DetailValue>{fullAddress || '—'}</DetailValue>
          </DetailRow>

          <DetailRow>
            <DetailIcon><StorefrontIcon sx={{ fontSize: 18 }} /></DetailIcon>
            <DetailLabel>Services</DetailLabel>
            <DetailValue>
              {servicesList.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {servicesList.map((service, idx) => (
                    <Chip
                      key={idx}
                      label={service}
                      size="small"
                      sx={{
                        bgcolor: '#dcfce7',
                        color: '#166534',
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Box>
              ) : '—'}
            </DetailValue>
          </DetailRow>

          {lead.package && (
            <DetailRow>
              <DetailIcon><CategoryIcon sx={{ fontSize: 18 }} /></DetailIcon>
              <DetailLabel>Selected Plan</DetailLabel>
              <DetailValue>
                <Chip
                  label={lead.package.toUpperCase()}
                  size="small"
                  sx={{
                    bgcolor: '#e0f2fe',
                    color: '#0369a1',
                    fontWeight: 600,
                  }}
                />
              </DetailValue>
            </DetailRow>
          )}

          {lead.gmb_account_id && (
            <DetailRow>
              <DetailIcon><StorefrontIcon sx={{ fontSize: 18 }} /></DetailIcon>
              <DetailLabel>GMB Profile</DetailLabel>
              <DetailValue sx={{ fontSize: '12px', color: '#6b7280' }}>
                ID: {lead.gmb_account_id}
              </DetailValue>
            </DetailRow>
          )}

          {lead.comment && (
            <DetailRow>
              <DetailIcon><CommentIcon sx={{ fontSize: 18 }} /></DetailIcon>
              <DetailLabel>Comment</DetailLabel>
              <DetailValue sx={{ color: '#6b7280' }}>{lead.comment}</DetailValue>
            </DetailRow>
          )}
        </InfoCard>

        {/* Activity Card */}
        <InfoCard>
          <CardHeader>
            <CardIconBox bgcolor="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)">
              <HistoryIcon sx={{ fontSize: 20 }} />
            </CardIconBox>
            <Typography variant="h6" fontWeight={600} color="#1f2937">Activity Timeline</Typography>
          </CardHeader>

          {rawActivities.length > 0 ? (
            rawActivities.map((item, index) => {
              const byName = item?.addedby?.full_name || item?.addedBy?.full_name || item?.user?.full_name || 'Unknown';
              const when = item?.createdAt || item?.updatedAt || null;
              const activityLabel = item?.activity || item?.activity_type || 'Activity';
              const whenStr = when
                ? new Date(when).toLocaleString(undefined, {
                  weekday: 'short',
                  year: 'numeric',
                  month: 'short',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })
                : '';

              return (
                <ActivityItem key={index}>
                  <ActivityAvatar>{getInitials(byName)}</ActivityAvatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={500} color="#1f2937">
                      {activityLabel}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      by <Typography component="span" color="#3b82f6" fontWeight={500}>{byName}</Typography>
                    </Typography>
                    {whenStr && (
                      <Typography variant="caption" color="#9ca3af" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <ScheduleIcon sx={{ fontSize: 12 }} /> {whenStr}
                      </Typography>
                    )}
                  </Box>
                </ActivityItem>
              );
            })
          ) : (
            <ActivityItem>
              <ActivityAvatar>
                {getInitials(lead?.createdByUser?.full_name || '?')}
              </ActivityAvatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={500} color="#1f2937">
                  Lead Created
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  by <Typography component="span" color="#3b82f6" fontWeight={500}>
                    {lead?.createdByUser?.full_name || 'System'}
                  </Typography>
                </Typography>
                {(lead?.createdAt || lead?.updatedAt) && (
                  <Typography variant="caption" color="#9ca3af" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <ScheduleIcon sx={{ fontSize: 12 }} />
                    {new Date(lead?.createdAt || lead?.updatedAt).toLocaleString(undefined, {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                )}
              </Box>
            </ActivityItem>
          )}
        </InfoCard>
      </Box>

      {/* Schedule Meeting Section */}
      {!isScheduling ? (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <ScheduleButton
            startIcon={<CalendarMonthIcon />}
            onClick={() => setIsScheduling(true)}
          >
            Schedule Meeting
          </ScheduleButton>
        </Box>
      ) : (
        <MeetingCard>
          <CardHeader>
            <CardIconBox bgcolor="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)">
              <EventIcon sx={{ fontSize: 20 }} />
            </CardIconBox>
            <Typography variant="h6" fontWeight={600} color="#1f2937">Schedule Meeting</Typography>
          </CardHeader>

          {scheduleError && (
            <Box sx={{
              bgcolor: '#fef2f2',
              color: '#dc2626',
              p: 2,
              borderRadius: '10px',
              mb: 2,
              fontSize: '14px',
            }}>
              {scheduleError}
            </Box>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label="Meeting Date & Time"
              name="fromDate"
              type="datetime-local"
              value={formState.fromDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <EventIcon sx={{ color: '#9ca3af', mr: 1 }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Select BDM</InputLabel>
              <Select
                name="bdm"
                value={formState.bdm}
                onChange={handleChange}
                label="Select BDM"
                disabled={bdmLoading}
                startAdornment={<GroupIcon sx={{ color: '#9ca3af', mr: 1 }} />}
                sx={{
                  borderRadius: '10px',
                }}
              >
                <MenuItem value="">
                  <em>Select BDM</em>
                </MenuItem>
                {showSelfBdm && (
                  <MenuItem key="bdm-self" value={employeeData.id}>
                    {selfBdmLabel}
                  </MenuItem>
                )}
                {bdms.map(bdm => (
                  <MenuItem key={bdm.id || bdm.employee_id} value={bdm.id || bdm.employee_id}>
                    {`${bdm.first_name || ''} ${bdm.last_name || ''}`.trim()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 2 }}>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setIsScheduling(false)}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleScheduleMeeting}
              disabled={scheduleLoading}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                },
              }}
            >
              {scheduleLoading ? 'Scheduling...' : 'Confirm Meeting'}
            </Button>
          </Box>
        </MeetingCard>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            bgcolor: '#10B981',
            color: 'white',
            px: 2.5,
            py: 1.5,
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(16, 185, 129, 0.3)',
            minWidth: '280px',
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 24 }} />
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>Meeting Scheduled!</Typography>
            <Typography sx={{ fontSize: '12px', opacity: 0.9 }}>Successfully created meeting</Typography>
          </Box>
        </Box>
      </Snackbar>
    </Container>
  );
};

export default LeadProfile;
